import React from 'react';
import { 
  Eye, 
  BarChart3, 
  FileText, 
  TrendingUp,
  Clock,
  Info,
  Lock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, Badge } from '@plvplus/core';
import { User } from '../../hooks/useUserRoles';

interface ViewerDashboardProps {
  user: User;
}

export const ViewerDashboard: React.FC<ViewerDashboardProps> = ({ user }) => {
  // Mock viewer data (read-only)
  const viewStats = [
    {
      name: 'Rapports consultés',
      value: '15',
      change: '+2',
      changeType: 'positive' as const,
      icon: BarChart3,
      description: 'Ce mois'
    },
    {
      name: 'Devis visualisés',
      value: '28',
      change: '+5',
      changeType: 'positive' as const,
      icon: FileText,
      description: 'Accès lecture'
    },
    {
      name: 'Dernière connexion',
      value: '2h',
      change: 'Actif',
      changeType: 'neutral' as const,
      icon: Clock,
      description: 'Session en cours'
    },
    {
      name: 'Niveau d\'accès',
      value: 'Lecture',
      change: 'Limité',
      changeType: 'neutral' as const,
      icon: Eye,
      description: 'Permissions'
    }
  ];

  const recentViews = [
    {
      id: '1',
      type: 'report',
      title: 'Rapport mensuel des ventes',
      description: 'Analyse des performances commerciales',
      viewedAt: '1h',
      category: 'Commercial'
    },
    {
      id: '2',
      type: 'calculation',
      title: 'Calcul PLV Pharmacie Martin',
      description: 'Présentoir cosmétiques - €1,562.50',
      viewedAt: '3h',
      category: 'PRI+'
    },
    {
      id: '3',
      type: 'quote',
      title: 'Devis Carrefour Toulouse',
      description: 'Gondole parfums - €2,730.00',
      viewedAt: '1j',
      category: 'Devis'
    }
  ];

  const availableReports = [
    {
      id: '1',
      title: 'Dashboard Commercial',
      description: 'Vue d\'ensemble des ventes et performances',
      category: 'Commercial',
      lastUpdated: '2h'
    },
    {
      id: '2',
      title: 'Analyse des Marges',
      description: 'Évolution des marges par produit et client',
      category: 'Finance',
      lastUpdated: '1j'
    },
    {
      id: '3',
      title: 'Suivi des Projets',
      description: 'État d\'avancement des projets en cours',
      category: 'Projets',
      lastUpdated: '3h'
    }
  ];

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'report': return BarChart3;
      case 'calculation': return TrendingUp;
      case 'quote': return FileText;
      default: return Eye;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Commercial': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
      case 'Finance': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      case 'PRI+': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300';
      case 'Devis': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300';
      case 'Projets': return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
    }
  };

  return (
    <div className="space-y-6">
      {/* Viewer Header */}
      <div className="bg-gradient-to-r from-gray-600 to-slate-600 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">
              Tableau de bord Consultation 👁️
            </h1>
            <p className="text-gray-100">
              Bienvenue {user.name.split(' ')[0]}, vous avez accès en lecture aux données PLV+.
            </p>
          </div>
          <div className="hidden md:block">
            <div className="text-right">
              <p className="text-sm text-gray-200">Niveau d'accès</p>
              <Badge variant="secondary" className="bg-white/20 text-white border-white/30">
                <Eye className="h-3 w-3 mr-1" />
                Lecture seule
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Access Notice */}
      <Card variant="outlined" className="border-blue-200 dark:border-blue-800">
        <CardContent className="p-4">
          <div className="flex items-start space-x-3">
            <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-blue-900 dark:text-blue-100">
                Accès en lecture seule
              </h3>
              <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                Vous pouvez consulter les rapports et données, mais ne pouvez pas les modifier. 
                Contactez votre administrateur pour étendre vos permissions.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* View Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {viewStats.map((stat) => (
          <Card key={stat.name} variant="elevated">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    {stat.name}
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {stat.value}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {stat.description}
                  </p>
                </div>
                <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-full">
                  <stat.icon className="h-6 w-6 text-gray-600 dark:text-gray-400" />
                </div>
              </div>
              <div className="mt-4">
                <span className={`text-sm font-medium ${
                  stat.changeType === 'positive' 
                    ? 'text-green-600 dark:text-green-400' 
                    : 'text-gray-600 dark:text-gray-400'
                }`}>
                  {stat.change}
                </span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Views */}
        <Card variant="elevated">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Clock className="h-5 w-5" />
              <span>Consultations récentes</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentViews.map((view) => {
                const Icon = getTypeIcon(view.type);
                return (
                  <div key={view.id} className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                    <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                      <Icon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                        {view.title}
                      </h4>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        {view.description}
                      </p>
                      <div className="flex items-center justify-between mt-2">
                        <Badge 
                          variant="outline" 
                          className={getCategoryColor(view.category)}
                        >
                          {view.category}
                        </Badge>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          Il y a {view.viewedAt}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Available Reports */}
        <Card variant="elevated">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <BarChart3 className="h-5 w-5" />
              <span>Rapports disponibles</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {availableReports.map((report) => (
                <div key={report.id} className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                        {report.title}
                      </h4>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        {report.description}
                      </p>
                      <div className="flex items-center justify-between mt-2">
                        <Badge 
                          variant="outline" 
                          className={getCategoryColor(report.category)}
                        >
                          {report.category}
                        </Badge>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          Mis à jour il y a {report.lastUpdated}
                        </span>
                      </div>
                    </div>
                    <Eye className="h-4 w-4 text-gray-400 ml-2" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Restricted Actions Notice */}
      <Card variant="outlined" className="border-yellow-200 dark:border-yellow-800">
        <CardContent className="p-6">
          <div className="flex items-center justify-center space-x-3">
            <Lock className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
            <div className="text-center">
              <h3 className="text-lg font-medium text-yellow-900 dark:text-yellow-100">
                Actions limitées
              </h3>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                En tant qu'utilisateur en lecture seule, vous ne pouvez pas créer ou modifier de contenu.
                Vous avez accès à tous les rapports et données en consultation.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
